import { Injectable, inject } from '@angular/core';
import {
  Customer,
  CustomerInfo,
  CustomerLibConfigurable,
  CustomerWithVehicles
} from '@service-and-repairs/calm-leads-customer-lib-api';
import {OidcConfigProvider} from '@oidc/OidcConfigProvider';
import {OidcFlow} from '@assets/OidcFlow.v2';
import {AuthenticationTokenBearerService} from '../../../auth/authentication-token-bearer.service';
import {OidcToken} from '../../../auth/oidc/oidc-token';
import {DmsService} from '../../../components/dms-search/dms.service';
import {Util} from '../../../util/util';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {fetchAllCustomersTestData, searchByCustomerNameTestData} from './customer-service-test-data';

/**
 * Wraps Calm Leads customer lib to search customers and fetch their vehicles, wiring it to current auth/DMS context.
 *
 * Lib loading/config: lazily loads /ui/customer/customer-impl.min.js, builds customerLib, and configures it with auth
 * headers (Auth-Dist-Partner, Auth-Outlet, bearer headers) plus the current dmsApi. Reconfigures on OIDC token changes
 * and DMS settings changes.
 *
 * Localhost shortcut: when running on localhost (except Karma ports 9876/9877), returns static test data for both
 * searchByCustomerName and fetchAllCustomers.
 *
 * Public API:
 *
 * searchByCustomerName(firstName, lastName): returns test data locally; otherwise delegates to the loaded lib or
 * rejects if unavailable.
 *
 * fetchAllCustomers(vin17): same pattern—test data locally, else delegate or reject.
 *
 * subscribeToSetPreferredCustomer(cb): waits for lib load, then subscribes to preferred-customer updates.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private readonly dmsService: DmsService = inject(DmsService);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService = inject(AuthenticationTokenBearerService);
  private readonly userService: UserService = inject(UserService);

  customerLibLoaded: Promise<CustomerLibConfigurable>;

  private customerLib: CustomerLibConfigurable;

  constructor() {
    this.customerLibLoaded = new Promise(resolve => {
      this.loadCustomerLibImplementation().then(() => {
        this.configureCustomerLib();
        resolve(this.customerLib);
      });
    });

    this.authenticationTokenBearerService.oidcTokenSubject.subscribe(this.configureCustomerLib.bind(this));
    this.dmsService.dmsSettingsChanged.subscribe(this.configureCustomerLib.bind(this));
  }

  async searchByCustomerName(firstName: string, lastName: string): Promise<CustomerWithVehicles[]> {
    if (this.runningOnLocalhost()) {
      return searchByCustomerNameTestData;
    }

    if (!this.customerLib) {
      return Promise.reject(new Error('Customer lib not loaded.'));
    }

    await this.ensureCustomerLibHasFreshOidcToken();
    return this.customerLib.searchByCustomerName(firstName, lastName);
  }

  async fetchAllCustomers(vin17: string): Promise<CustomerInfo> {
    if (this.runningOnLocalhost()) {
      return fetchAllCustomersTestData;
    }

    if (!this.customerLib) {
      return Promise.reject(new Error('Customer lib not loaded.'));
    }

    await this.ensureCustomerLibHasFreshOidcToken();
    return this.customerLib.fetchAllCustomers(vin17);
  }

  subscribeToSetPreferredCustomer(cb: (vin: string, customers: Customer[]) => void): void {
    this.customerLibLoaded.then((): void => {
      this.customerLib.subscribeToSetPreferredCustomer(cb);
    });
  };

  // returns true if AWP is running locally (excluding unit tests)
  private runningOnLocalhost(): boolean {
    return window.location.hostname === 'localhost' &&
      window.location.port !== '9876' &&
      window.location.port !== '9877';
  }

  private loadCustomerLibImplementation(): Promise<void> {
    if (this.customerLib) {
      // already loaded
      return Promise.resolve();
    }
    return new Promise(resolve => Util.attachScript('/ui/customer/customer-impl.min.js')
      .then(() => {
        if (window['calmLeadsCustomerImpl']) {
          this.customerLib = window['calmLeadsCustomerImpl'].makeCustomerLib();
        }
        resolve();
      }));
  }

  /**
   * Ensures the customer lib holds a valid OIDC token right before a request is made.
   *
   * Customer lib requests bypass the AWP {@link WenInterceptor}, so they never trigger a token refresh themselves and
   * rely solely on the lib having been (re)configured with a fresh token. The proactive refresh in OidcFlow runs on a
   * background timer that browsers throttle/freeze in inactive tabs, so a long-open tab can end up sending an expired
   * token, resulting in a 401 from the gateway. To avoid that, we:
   *   1. refresh the OIDC token if it has expired (drives the regular oidcTokenRefreshed reconfigure flow), and
   *   2. reconfigure the lib from the freshest token in storage as the source of truth.
   */
  private async ensureCustomerLibHasFreshOidcToken(): Promise<void> {
    const oidc: OidcFlow = new OidcFlow(new OidcConfigProvider().get(window.location.hostname));
    if (oidc.oidcIsSupported() && oidc.getOidcTokenFromStorage()) {
      try {
        await oidc.refreshAccessTokenIfExpired();
      } catch {
        console.warn(Util.logPrefix('Webapp', 'Auth')
          + 'Could not ensure a fresh OIDC token before calling the customer lib. Proceeding with existing token.');
      }
    }
    this.configureCustomerLib();
  }

  private configureCustomerLib(): void {
    const userData: UserData = this.userService.getCurrentUser();

    const oidcToken: OidcToken = this.getCurrentOidcToken(userData);

    if (userData && oidcToken.access_token) {
      this.customerLib?.configure(
        this.getHeadersForCustomerLib(oidcToken, userData),
        this.dmsService.dmsApi as any,
        undefined
      );
    }
  }

  /**
   * Returns the freshest known OIDC token, preferring the one in storage (kept up to date by the refresh flow and the
   * single source of truth used by the WenInterceptor) and falling back to the published token subject. The business
   * context is taken from the active user, which stays constant for the whole session.
   */
  private getCurrentOidcToken(userData: UserData): OidcToken {
    const storedToken: OidcToken = userData
      ? OidcToken.readFromStorage(userData.getContext())
      : undefined;
    return storedToken?.access_token
      ? storedToken
      : this.authenticationTokenBearerService.oidcTokenSubject.getValue();
  }

  private getHeadersForCustomerLib(oidcToken: OidcToken, userData: UserData): {[key: string]: string;} {
    const headers = oidcToken.getHeaders();
    headers['Auth-Dist-Partner'] = userData.getBusinessPartner()?.getDistributionPartnerNumber();
    headers['Auth-Outlet'] = userData.getBusinessPartner()?.getOutletNumber();

    return headers;
  }
}
